<script lang="ts" setup>
import {
  getEvent,
  getEvent_getEvent_GetEventOutput_event,
  getEvent_getEvent_GetEventOutput_event_admissions,
  getEvent_getEvent_GetEventOutput_event_eventAttributes,
  getEvent_getEvent_GetEventOutput_event_admissions_admissionType_GetAdmissionTypeOutput_admissionType
} from '__generated__/getEvent'
import { listAnnouncements } from '__generated__/listAnnouncements'
import {
  EventSortField,
  SortOrder,
  EventStatus,
  EventPurchaseType
} from '__generated__/globalTypes'
import { getReviews } from '__generated__/getReviews'
import { getEvents } from '__generated__/getEvents'
import { newWaitlistUser } from '__generated__/newWaitlistUser'
import { getWaitlistUsers } from '__generated__/getWaitlistUsers'

import { ref, watch, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'

import { Card, CardType } from '@base/_types/Card'
import { ReviewCard as ReviewCardType } from '@base/_types/ReviewCard'

import { useClient } from '@base/api/useClient'

import { createDistanceContent } from '@base/lib/createDistanceContent'
import getSlug from '@base/lib/getSlug'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'
import { createSlug } from '@base/lib/createSlug'

import { useRouterStore } from '@base/store/routerStore'
import { useToastsStore } from '@base/store/toastsStore'
import { useUserStore } from '@base/store/userStore'

import { Toast, ToastSemantic } from '@base/_types/Toast'

import BadgePremium from '@base/components/BadgePremium/BadgePremium.vue'
import CardRow from '@base/components/CardRow/CardRow.vue'
import ClientOnly from '@base/components/ClientOnly/ClientOnly.vue'
import ClubDetails from '@base/components/ClubDetails/ClubDetails.vue'
import HeaderContent from '@base/components/HeaderContent/HeaderContent.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import LinkWrapper from '@base/components/LinkWrapper/LinkWrapper.vue'
import ReviewCardStack from '@base/components/ReviewCardStack/ReviewCardStack.vue'
import USTextSlider from '@base/components/USTextSlider/USTextSlider.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USMediaSlide from '@base/components/USMediaSlide/USMediaSlide.vue'
import USMediaSlider from '@base/components/USMediaSlider/USMediaSlider.vue'
import USShareButton from '@base/components/USShareButton/USShareButton.vue'
import USTextTags from '@base/components/USTextTags/USTextTags.vue'

import { formatDate } from '@base/helpers/formatDate'

import eventPlaceholder from '@player/_assets/placeholder.jpg'

import { playerTitle } from '@player/_lib/playerTitle'

import { accountRouteNames } from '@player/account/router/routeNames'

import { bookingsRouteNames } from '@player/bookings/router/routeNames'

import { ANNOUNCEMENT_QUERY } from '@player/clubs/api/queries/getAnnouncements'
import { REVIEWS_QUERY } from '@base/api/queries/getReviews'

import { clubsRouteNames } from '@player/clubs/router/routeNames'

import { GET_EVENT_QUERY } from '@player/events/api/queries/getEvent'
import { LIST_EVENTS } from '@player/events/api/queries/listEvents'
import { eventsRouteNames } from '@player/events/router/routeNames'

import imageEventPlaceholder from '@player/_assets/event-placeholder.jpg'

import SponsorIconGrid from '../components/SponsorIconGrid/SponsorIconGrid.vue'

import placeholder from '../assets/placeholder.png'

import { props as propDefinition } from './props'

import { NEW_WAITLIST_USER } from '../api/mutations/newWaitlistUser'
import { GET_WAITLIST_USERS } from '../api/queries/getWaitlistUsers'

type SponsorImage = {
  src: string
  alt: string
}

type unsortedAdmissionType = {
  admission: getEvent_getEvent_GetEventOutput_event_admissions_admissionType_GetAdmissionTypeOutput_admissionType
  price: number
}

// Props
const props = defineProps(propDefinition)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()
const routerStore = useRouterStore()
const userStore = useUserStore()
const toastsStore = useToastsStore()
const timeZone = 'America/Denver';

// Outputs
const loading = ref<boolean>(false)
const serverError = ref<boolean>(false)
const currentURL = ref('')
const date = ref<Date>(new Date())
const fullClubAddress = ref('')
date.value.setHours(0, 0, 0)
const pageTitle = ref(playerTitle())
const eventData = ref<getEvent_getEvent_GetEventOutput_event | null>(null)
const announcements = ref<string[]>([])
const sortedAdmissionTypes = ref<unsortedAdmissionType[] | null>()
const unsortedAdmissionTypes = ref<unsortedAdmissionType[]>([])
const premiumPoints = ref<number>(0)
const relatedEventsCards = ref<Card[]>([])
const reviewCards = ref<ReviewCardType[]>([])
const reviewQueryOffset = ref<number>(0)
const clubId = ref<string>('')
const waitlistButtonDisabled = ref<boolean>(false)
const sponsors = ref<SponsorImage[]>([])

useHead({
  title: pageTitle
})

let end = new Date(date.value.getTime() + 86400000)

// Helper Functions
const reset = () => {
  loading.value = false
  serverError.value = false
  date.value.setHours(0, 0, 0)
  reviewCards.value = []
}

const convertStartType = (startType: string) => {
  if (startType === 'SHOTGUN_START') {
    return 'Shotgun Start'
  } else if (startType === 'TEE_OFF_START') {
    return 'Tee Off Start'
  }
}

const memberPrice = (admissionPrice: number, memberDiscount: number) => {
  let discount = admissionPrice * (memberDiscount / 100)
  return admissionPrice - discount
}

const handleToastSuccess = () => {
  toastsStore.pushToast({
    content: 'The URL has been copied to clipboard!',
    icon: 'check-circle',
    semantic: ToastSemantic.Success
  })
}

const clubAddress = (
  addressLine1: string,
  addressLine2: string,
  city: string,
  province: string,
  postalCode: string
) => {
  let fullAddress = ''

  if (addressLine1) {
    fullAddress += addressLine1 + ', '
  }

  if (addressLine2) {
    fullAddress += addressLine2 + ', '
  }

  if (city) {
    fullAddress += city + ', '
  }

  if (province) {
    fullAddress += province + ', '
  }

  if (postalCode) {
    fullAddress += postalCode
  }
  fullClubAddress.value = fullAddress
}

const generateClubLogo = (logo: string) => {
  return {
    src: ugcImageSrc(logo)[0]
  }
}

const findAttributesLabels = (
  attributes: [getEvent_getEvent_GetEventOutput_event_eventAttributes]
) => {
  let attributesLabels = <string[]>[]
  attributes.forEach((attr) => {
    attributesLabels.push(attr.name)
  })
  return attributesLabels
}


// Query Functions

const fetchReviews = async () => {
  if (!clubId.value) return

  try {
    const { data: clubReviews } = await client.query<getReviews>({
      query: REVIEWS_QUERY,
      fetchPolicy: 'no-cache',
      variables: {
        clubId: clubId.value,
        offset: reviewQueryOffset.value
      }
    })

    if (clubReviews.getReviews.__typename !== 'GetReviewsOutput') {
      return
    }

    clubReviews.getReviews.reviews?.forEach((review) => {
      let tags = <string[]>[]
      let username = 'unknown'
      let avatar = ''

      review.attributes?.forEach((tag) => {
        tags.push(tag.name)
      })

      if (
        review.user.__typename === 'GetUserOutput' &&
        review.user.user !== null
      ) {
        username = review.user.user.username
        avatar = ugcImageSrc(review.user.user.profilePicture || '')[0]
      }

      reviewCards.value.push({
        user: {
          username: username,
          avatar: avatar
        },
        bookedDate: new Date(review.createdAt),
        rating: review.rating,
        showDropdown: false,
        tags: tags,
        review: review.body || '',
        images: review.galleryPaths?.map((img) => {
          return { src: ugcImageSrc(img)[0] }
        })
      })
    })
  } catch (error) {
    console.error('[EventSingle] fetchReviews - Error:', error)
    throw error
  }
}

const fetchData = async () => {
  reset()
  if (!props.slug) {
    routerStore.is404 = true
    return
  }

  const slug = getSlug(props.slug)

  try {
    loading.value = true
    announcements.value = []
    sortedAdmissionTypes.value = null
    premiumPoints.value = 0
    relatedEventsCards.value = []
    
    const { data: eventValue } = await client.query<getEvent>({
      query: GET_EVENT_QUERY,
      variables: {
        slug: slug
      },
      fetchPolicy: 'cache-first'
    })

    if (
      !eventValue ||
      eventValue.getEvent.__typename !== 'GetEventOutput' ||
      !eventValue.getEvent.event ||
      eventValue.getEvent.event.club.__typename !== 'GetClubOutput' ||
      !eventValue.getEvent.event.club
    ) {
      routerStore.is404 = true
      return
    }

    const { data: announcementsValue } = await client.query<listAnnouncements>({
      query: ANNOUNCEMENT_QUERY,
      variables: {
        eventSlug: eventValue.getEvent.event.slug
      },
      fetchPolicy: 'cache-first'
    })

    if (
      announcementsValue.listAnnouncements.__typename !==
      'ListAnnouncementsOutput'
    ) {
      routerStore.is404 = true
      return
    }

    eventData.value = eventValue.getEvent.event
    pageTitle.value = playerTitle(eventValue.getEvent.event.name)

    const { data: relatedEvents } = await client.query<getEvents>({
      query: LIST_EVENTS,
      variables: {
        filter: {
          dateStartAfter: new Date(new Date().setHours(0, 0, 0))
        },
        sort: {
          sortField: EventSortField.DATE_START,
          sortOrder: SortOrder.ASC
        },
        limit: 10
      },
      fetchPolicy: 'no-cache'
    })

    if (relatedEvents.listEvents.__typename !== 'ListEventsOutput') {
      routerStore.is404 = true
      return
    }

    clubId.value = eventValue.getEvent.event.club.club?.id || ''

    relatedEvents.listEvents.events.forEach((event) => {
      if (event?.club.__typename !== 'GetClubOutput' || !event.club.club) {
        return
      }

      relatedEventsCards.value.push({
        type: CardType.CardEvent,
        data: {
          eventName: event.name,
          eventDate: new Date(event.dateStart),
          clubName: event.club.club.clubName || '',
          exclusive: event.exclusive || false,
          image: {
            src:
              event.gallery && event.gallery.length
                ? ugcImageSrc(event.gallery[0] || '')[0]
                : imageEventPlaceholder
          },
          to: {
            name: eventsRouteNames.EventSingle,
            params: {
              slug: createSlug(event.name, event.slug)
            }
          }
        }
      })
    })

    if (
      eventValue.getEvent.event.eventWaitlistUsers &&
      eventValue.getEvent.event.eventWaitlistUsers.length
    ) {
      eventValue.getEvent.event.eventWaitlistUsers?.find((user) => {
        if (user.id === userStore.id) {
          waitlistButtonDisabled.value = true
        }
      })
    }

    await fetchReviews()

    clubAddress(
      eventValue.getEvent.event.club.club?.addressLine1 || '',
      eventValue.getEvent.event.club.club?.addressLine2 || '',
      eventValue.getEvent.event.club.club?.city || '',
      eventValue.getEvent.event.club.club?.province || '',
      eventValue.getEvent.event.club.club?.postalCode || ''
    )

    announcementsValue.listAnnouncements.announcements.forEach((item) => {
      announcements.value.push(item.content)
    })

    if (eventData.value.admissions) {
      unsortedAdmissionTypes.value = []

      eventData.value.admissions.forEach((admission) => {
        if (admission.admissionType.__typename === 'GetAdmissionTypeOutput') {
          unsortedAdmissionTypes.value.push({
            admission: admission.admissionType.admissionType,
            price: admission.price
          })
        }
      })

      sortedAdmissionTypes.value = unsortedAdmissionTypes.value.sort(
        (a, b) => a.admission.position - b.admission.position
      )
    }

    let lowestCost = 0
    eventData.value.admissions?.forEach((item) => {
      if (lowestCost === 0) lowestCost = item.price
      if (lowestCost !== 0 && item.price < lowestCost) lowestCost = item.price
    })

    if (eventData.value.memberDiscount) {
      let discount = lowestCost / eventData.value.memberDiscount

      premiumPoints.value = Math.floor(lowestCost - discount)
    } else {
      premiumPoints.value = lowestCost
    }

    eventData.value.sponsors?.forEach((sponsor) => {
      sponsors.value.push({
        src: ugcImageSrc(sponsor)[0],
        alt: `Sponsor logo for ${eventData.value?.name}`
      })
    })
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}

// Mutation Functions

const handleJoinWaitlist = async () => {
  if (waitlistButtonDisabled.value) return

  let userId = userStore.id
  try {
    waitlistButtonDisabled.value = true

    if (userId) {
      let { data } = await client.mutate<newWaitlistUser>({
        mutation: NEW_WAITLIST_USER,
        variables: {
          userId: userId,
          eventId: eventData.value?.id
        }
      })

      if (
        !data ||
        data.newWaitlistUser.__typename !== 'NewWaitlistUserOutput' ||
        !data.newWaitlistUser.success
      ) {
        toastsStore.pushToast({
          content: 'There was an error adding you to the waitlist',
          semantic: ToastSemantic.Error,
          icon: 'exclamation-square'
        })
        return
      }

      toastsStore.pushToast({
        content:
          'You’ve been added to the waitlist and will be notified when a spot becomes available!',
        semantic: ToastSemantic.Success,
        icon: 'check-circle'
      })
    } else {
      toastsStore.pushToast({
        content:
          'It looks like you may not be signed in, if you are please refresh the page.',
        semantic: ToastSemantic.Error,
        icon: 'exclamation-square'
      })
      waitlistButtonDisabled.value = false
      return
    }
  } catch (e) {
    toastsStore.pushToast({
      content: 'There was an error adding you to the waitlist',
      semantic: ToastSemantic.Error,
      icon: 'exclamation-square'
    })
    waitlistButtonDisabled.value = false
  }
}

// User Interaction Handlers

const handleLoadMoreReviews = async () => {
  reviewQueryOffset.value = reviewQueryOffset.value + 10
  await fetchReviews()
}

// Initial Data Fetch

// Fetch Data when the URL changes the props
watch(props, async () => {
  await fetchData()
})

// Fetch data when the view is first rendered
await fetchData()

//
onMounted(() => {
  currentURL.value = window.location.href
})

// Function to track Register button clicks
const trackRegisterClick = () => {
  if (eventData.value?.purchaseType === EventPurchaseType.EXTERNAL){
    if (window.gtag) {  
      window.gtag("event", "ext_registration_click", {
        event_category: "External Event Registration",
        event_name: eventData.value?.name,
        event_id: eventData.value?.id
      });
    }
  }
}

// Registration opens at 7:00 AM Mountain Time on the registration start calendar day
// Registration closes at 23:59:59 Mountain Time on the registration end calendar day
// Uses native Intl API instead of date-fns-tz to avoid bundling issues
function getMSTDateComponents(date: Date): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  return formatter.format(date)
}

function mstWallClockToUtcInstant(mstDateStr: string, timeStr: string): Date {
  const mstDateTimeStr = `${mstDateStr}T${timeStr}`
  const testUTC = new Date(mstDateTimeStr + 'Z')
  const mstTime = testUTC.toLocaleString('en-US', {
    timeZone,
    hour12: false
  })
  const utcTime = testUTC.toLocaleString('en-US', {
    timeZone: 'UTC',
    hour12: false
  })
  const mstDate = new Date(mstTime)
  const utcDate = new Date(utcTime)
  const offsetMs = mstDate.getTime() - utcDate.getTime()
  return new Date(testUTC.getTime() - offsetMs)
}

function getRegistrationScheduleThresholds(
  registrationStart: string,
  registrationEnd: string
): { startThreshold: Date; endThreshold: Date } {
  const startDate = new Date(registrationStart)
  const startMSTDateStr = getMSTDateComponents(startDate)
  const startThreshold = mstWallClockToUtcInstant(startMSTDateStr, '07:00:00')

  const endDate = new Date(registrationEnd)
  const endMSTDateStr = getMSTDateComponents(endDate)
  const endThreshold = mstWallClockToUtcInstant(endMSTDateStr, '23:59:59')

  return { startThreshold, endThreshold }
}

type RegistrationCtaPhase =
  | 'not_yet_open'
  | 'closed'
  | 'paused'
  | 'open_full'
  | 'open_available'

const registrationCtaPhase = computed((): RegistrationCtaPhase => {
  const e = eventData.value
  if (!e) return 'not_yet_open'

  const start = e.registrationStart
  const end = e.registrationEnd
  if (!start || !end) return 'not_yet_open'

  const { startThreshold, endThreshold } = getRegistrationScheduleThresholds(
    start,
    end
  )
  const now = new Date()

  if (now < startThreshold) return 'not_yet_open'
  if (now >= endThreshold) return 'closed'
  if (e.status !== EventStatus.REGISTRATION_OPEN) return 'paused'
  if ((e.spotsRemaining ?? 0) <= 0) return 'open_full'
  return 'open_available'
})

const registrationDisabledHint = computed((): string => {
  const e = eventData.value
  if (!e) return ''

  const phase = registrationCtaPhase.value
  if (phase === 'open_available') return ''
  if (
    phase === 'open_full' &&
    e.purchaseType === EventPurchaseType.UPSWING
  ) {
    return ''
  }

  switch (phase) {
    case 'not_yet_open':
      if (!e.registrationStart || !e.registrationEnd) {
        return 'Registration is not yet available.'
      }
      return `Registration opens ${formatDate(new Date(e.registrationStart))} at 7:00 AM Mountain Time`
    case 'closed':
      return `Registration closed ${formatDate(new Date(e.registrationEnd))} at 11:59 PM Mountain Time`
    case 'paused':
      return 'Registration is currently paused.'
    case 'open_full':
      return 'This event is full.'
    default:
      return ''
  }
})

</script>

<template>
  <div v-if="eventData" class="event-single body--p1--regular">
    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <!---->
        <HeaderContent type="secondary">
          <template #secondary-left>
            <div class="event-single__header-labels text-transform--uppercase font-color--neutral-a3">
              <p class="event-single__header-label margin-right--6">
                {{ eventData.club.club.clubName }}
              </p>
              <p class="event-single__header-label margin-right--6">
                {{ eventData.club.club.city }}
              </p>
              <p class="event-single__header-label margin-right--6">
                {{ createDistanceContent(eventData.club.club.addressGeom) }}
              </p>
            </div>
          </template>

          <template #secondary-right>
            <USShareButton label="Share Event" :share-url="currentURL" @succuss="handleToastSuccess" />
          </template>
        </HeaderContent>

        <!---->
        <h2 class="heading--h2 margin-bottom--6">
          {{ eventData.name }}
        </h2>
      </div>
    </div>

    <!----->
    <USMediaSlider v-if="eventData.gallery.length" class="margin-bottom--8">
      <USMediaSlide v-for="(img, idx) in eventData.gallery.map((img) => ugcImageSrc(img))" :key="idx"
        :img-args="{ src: img[0], srcset: img[1] }" :lazy="true" />
    </USMediaSlider>
    <USMediaSlider v-else>
      <USMediaSlide :img-args="{ src: eventPlaceholder }" :lazy="true" />
    </USMediaSlider>

    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <!-- 
        <div v-if="eventData.eventAttributes.length" class="padding-bottom--5">
          <USTextTags :tags="findAttributesLabels(eventData.eventAttributes)" />
        </div>-->
        <div v-if="eventData?.customAttributes?.length" class="padding-bottom--5">
          <USTextTags :tags="eventData?.customAttributes" />
        </div>
        <USTextSlider v-if="announcements.length" class="margin-bottom--5" :texts="announcements" />
      </div>
    </div>

    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <p v-if="eventData.description" class="margin-bottom--6">
          {{ eventData.description }}
        </p>

        <div class="event-single__info-row margin-bottom--6">
          <div
            v-if="sortedAdmissionTypes && sortedAdmissionTypes.length && eventData.purchaseType === EventPurchaseType.UPSWING"
            class="event-single__info-column">
            <p class="body--p1--medium text-transform--uppercase font-color--neutral-a2 margin-bottom--7">
              Admission Fee
            </p>
            <template v-if="sortedAdmissionTypes.length">
              <div class="event-single__info">
                <template v-for="(admission, index) in sortedAdmissionTypes" :key="index">
                  <p class="event-single__info-item">
                    <span class="body--p1--medium">
                      {{ admission.admission.name }}:
                    </span>
                    <span>${{ admission.price }}</span>
                    <!--
                    <span>
                      ${{ admission.price }} | ${{
                        memberPrice(
                          admission.price,
                          eventData.memberDiscount
                        ).toFixed(2)
                      }}
                      (UpSwing Member)
                    </span>-->
                  </p>
                </template>
              </div>
            </template>
          </div>

          <!-- External Purchase Type -->
          <div v-if="eventData.purchaseType === EventPurchaseType.EXTERNAL" class="event-single__info-column">
            <p class="body--p1--medium text-transform--uppercase font-color--neutral-a2 margin-bottom--7">
              Admission Fee
            </p>
            <div class="event-single__info">
              <p class="event-single__info-item">
                <span v-if="eventData.externalPriceRangeDescription" class="body--p1--medium">
                  {{ eventData.externalPriceRangeDescription }}:
                </span>
                {{ eventData.externalPriceRange }}
              </p>
            </div>
          </div>


          <div class="event-single__info-column">
            <p class="body--p1--medium text-transform--uppercase font-color--neutral-a2 margin-bottom--7">
              Event Info
            </p>
            <ClientOnly>
              <div class="event-single__info">
                <div class="event-single__info-item">
                  <p v-if="eventData.dateStart">
                    <span class="body--p1--medium">
                      {{
                      eventData.dateStart === eventData.dateEnd
                      ? 'Date:'
                      : 'Start Date:'
                      }}
                    </span>
                    <span>
                      &nbsp;{{ formatDate(new Date(eventData.dateStart)) }}
                    </span>
                  </p>
                  <p v-if="eventData.dateStart != eventData.dateEnd">
                    <span class="body--p1--medium">End Date:</span>
                    <span>&nbsp;
                      {{ formatDate(new Date(eventData.dateEnd)) }}</span>
                  </p>
                  <p v-if="eventData?.startDateDescription">
                    <span class="body--p2 font-color--neutral-a2">{{ eventData?.startDateDescription }}</span>
                  </p>
                </div>
                <div class="event-single__info-item">
                  <p v-if="eventData.startTime">
                    <span class="body--p1--medium">Start Time: </span>
                    <span>
                      {{ eventData.startTime }}
                    </span>
                  </p>
                  <p v-if="eventData?.startTimeDescription">
                    <span class="body--p2 font-color--neutral-a2">{{ eventData?.startTimeDescription }}</span>
                  </p>
                </div>
                <div class="event-single__info-item">
                  <p v-if="eventData.registrationStart">
                    <span class="body--p1--medium">Registration Open: </span>
                    <span>
                      {{ formatDate(new Date(eventData.registrationStart)) }} at 7:00 AM Mountain Time
                    </span>
                  </p>
                  <p v-if="
                      eventData.registrationStart !== eventData.registrationEnd
                    ">
                    <span class="body--p1--medium">Registration Close:</span>
                    <span>
                      &nbsp;{{
                      formatDate(new Date(eventData.registrationEnd))
                      }}
                    </span>
                  </p>
                </div>
                <p v-if="eventData.startType && !eventData?.isNonGolf" class="event-single__info-item">
                  <span class="body--p1--medium">Start Type: </span>
                  <span>
                    {{ convertStartType(eventData.startType) }}
                  </span>
                </p>
              </div>
            </ClientOnly>
          </div>
        </div>

        <!---->
        <!--
        <BadgePremium v-if="!userStore.isPremium" class="margin-bottom--6">
          <template #content>
            <p>
              <span>Become an</span>
              <LinkWrapper
                :link="{
                  to: {
                    name: accountRouteNames.Membership
                  }
                }"
              >
                <span class="body--hyperlink--semibold">
                  UpSwing Premium Member
                </span>
              </LinkWrapper>
              <span>
                to access advanced booking and earn
                {{ premiumPoints }}
                points for this event.
              </span>
            </p>
          </template>
        </BadgePremium>-->

        <!---->
        <div class="event-single__register-row margin-bottom--4">

          <LinkWrapper
            v-if="registrationCtaPhase === 'open_available'"
            @click="trackRegisterClick"
            :link="eventData.purchaseType === EventPurchaseType.UPSWING ? {
              to: {
                name: bookingsRouteNames.BookingNew,
                query: { type: 'event', id: eventData.id }
              }
            } : { path: eventData.externalPurchaseLink || '', target: '_blank' }">
            <USButton label="Register For Event" />
          </LinkWrapper>

          <USButton
            v-else-if="
              registrationCtaPhase === 'open_full' &&
                eventData.purchaseType === EventPurchaseType.UPSWING
            "
            label="Join waitlist"
            :disabled="waitlistButtonDisabled"
            @click="handleJoinWaitlist"
          />

          <div
            v-else
            class="event-single__register-disabled"
          >
            <USButton
              is="button"
              label="Register For Event"
              :disabled="true"
            />
            <p
              v-if="registrationDisabledHint"
              class="body--p2 font-color--neutral-a2 margin-top--7"
            >
              {{ registrationDisabledHint }}
            </p>
          </div>

          <!--
          <template
            v-if="
              eventData.spotsRemaining > 0 &&
              eventData.advancedBookingWindow &&
              advanceBookingWindow
            "
          >
            <ClientOnly>
              <p class="margin-left--6 body--p1--medium font-color--neutral-a3">
                <span>
                  Advanced booking open for UpSwing Premium Members! General
                  booking open in
                  {{ advanceBookingWindow }}
                </span>
              </p>
            </ClientOnly>
          </template>-->


        </div>
        <SponsorIconGrid v-if="sponsors && sponsors.length" class="margin-bottom--4"
          :up-swing-sponsored="eventData.exclusive" :sponsors="sponsors" />
      </div>
    </div>
    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <h3 class="heading--h3 margin-bottom--6">Related Events</h3>
      </div>
    </div>

    <CardRow v-if="relatedEventsCards && relatedEventsCards.length" class="margin-bottom--4"
      :cards="relatedEventsCards" />

    <div v-if="reviewCards && reviewCards.length" class="grid__row">
      <div class="grid__column grid__column--12">
        <h3 class="heading--h3 margin-bottom--6">Reviews</h3>

        <ReviewCardStack class="margin-bottom--6" :reviews="reviewCards" />

        <div class="text-align--center margin-bottom--4">
          <USButton type="inline" label="View More" @click="handleLoadMoreReviews" />
        </div>
      </div>
    </div>

    <ClubDetails heading="Visit the Club" :amenities="eventData.club.club.amenities"
      :club-avatar="generateClubLogo(eventData.club.club.logo)" :club-name="eventData.club.club.clubName"
      :club-address="fullClubAddress" :club-rating="eventData.club.club.averageRating || 0"
      :club-phone-number="eventData.club.club.phoneNumber" :to="{
        name: clubsRouteNames.ClubSingle,
        params: {
          slug: createSlug(
            eventData.club.club.clubName,
            eventData.club.club.slug
          )
        }
      }" />
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

.event-single {
  &__header-labels {
    color: var(--color--neutral-a3);
    display: flex;
    flex-direction: column-reverse;
    margin-top: var(--spacing--8);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      flex-direction: row;
      justify-content: space-between;
      margin-top: 0;
    }
  }

  &__header-label {
    &:not(:last-child) {
      margin-bottom: var(--spacing--8);

      @media (min-width: breakpoint.$breakpoint--tablet) {
        margin-bottom: 0;
      }
    }
  }

  &__info-row {
    display: flex;
    flex-direction: column;
  }

  &__info-column {
    width: 100%;

    &:first-child {
      margin-right: var(--spacing--5);
    }

    &:last-child {
      margin-top: var(--spacing--6);
    }
  }

  &__info {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    column-gap:  var(--spacing--5);

    @media (min-width: breakpoint.$breakpoint--tablet) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: breakpoint.$breakpoint--desktop) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__info-item {
    &:not(:last-child) {
      margin-bottom: var(--spacing--7);
    }
  }

  &__register-row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &__register-disabled {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  }
}
</style>